// Sugalarm — on-screen keyboard for the kiosk frame (cycle 14).
//
// Slides up from the bottom (above the FooterTabs nav, below the
// UrgentOverlay) when any text input or textarea inside the kiosk
// gains focus. Slides down on its own Hide button or when focus
// moves to a non-text element.
//
// React-friendly value injection: we go through the native
// `Object.getOwnPropertyDescriptor(...).set` so React's controlled
// input adapter sees the change as a real synthetic event. Setting
// `el.value = …` directly skips React's hooks.
//
// Mounted only on the kiosk variant (the web frame has a real
// keyboard). The component owns its visibility — nothing else needs
// to know it exists.

import { useCallback, useEffect, useRef, useState } from "react";

import {
  liftFieldAboveKeyboard,
  releaseKeyboardLift,
} from "../../lib/kioskKeyboardScroll";
import styles from "./OnScreenKeyboard.module.css";

// Margin so the focused field never sits flush against the keyboard's top.
const KBD_CLEARANCE_PX = 24;

type EditableElement = HTMLInputElement | HTMLTextAreaElement;

interface Props {
  /** Cycle 77: kiosk nav position. In "bottom" mode the keyboard sits
   *  above the floating FooterTabs pill (the cycle-14/45 behavior,
   *  unchanged). In "side" mode there's no bottom pill, so it slides up
   *  flush from the bottom edge of the content area. */
  navPosition?: "bottom" | "side";
}

const TEXT_TYPES = new Set([
  "text",
  "email",
  "password",
  "search",
  "tel",
  "url",
]);

function isTextEditable(el: Element | null): el is EditableElement {
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (el instanceof HTMLInputElement) {
    if (el.readOnly || el.disabled) return false;
    return TEXT_TYPES.has(el.type.toLowerCase());
  }
  return false;
}

const ROW_LETTERS_LOWER: string[][] = [
  "qwertyuiop".split(""),
  "asdfghjkl".split(""),
  "zxcvbnm".split(""),
];

const ROW_NUMBERS: string[][] = [
  "1234567890".split(""),
  "-/:;()$&@\"".split(""),
  ".,?!'".split(""),
];

type Mode = "letters" | "numbers";

export function OnScreenKeyboard({ navPosition = "bottom" }: Props) {
  const [open, setOpen] = useState(false);
  const [shift, setShift] = useState(false);
  const [mode, setMode] = useState<Mode>("letters");
  const focusedRef = useRef<EditableElement | null>(null);
  const kbdRef = useRef<HTMLDivElement | null>(null);
  // The keyboard's nav position drives how much of the bottom it occludes
  // (bottom mode floats 64px above the edge; side mode is flush at 0).
  const navPositionRef = useRef<"bottom" | "side">("bottom");

  // Track focus on text-editable elements so we know which target to
  // mutate when the user taps a key.
  useEffect(() => {
    const onFocusIn = (e: FocusEvent) => {
      const t = e.target as Element;
      if (isTextEditable(t)) {
        focusedRef.current = t;
        setOpen(true);
        // Wait for the slide-up to settle, then lift the field clear of the
        // keyboard (cycle 277). The occluded bottom band is the keyboard's
        // own height plus its bottom offset; `kioskKeyboardScroll` makes the
        // `.kioskContent` ancestor scrollable, reserves that much bottom
        // room, and scrolls the field up so it's never behind the keyboard.
        window.setTimeout(() => {
          const kbd = kbdRef.current;
          const bottomOffset = navPositionRef.current === "side" ? 0 : 64;
          const inset = kbd
            ? bottomOffset + kbd.offsetHeight + KBD_CLEARANCE_PX
            : 280;
          liftFieldAboveKeyboard(t, inset);
        }, 220);
      } else {
        // Focus moved to a non-text element — close the keyboard + restore.
        focusedRef.current = null;
        setOpen(false);
        setShift(false);
        releaseKeyboardLift();
      }
    };

    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      releaseKeyboardLift();
    };
  }, []);

  const insert = useCallback((char: string) => {
    const el = focusedRef.current;
    if (!el) return;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const next = el.value.slice(0, start) + char + el.value.slice(end);
    setReactValue(el, next);
    const cursor = start + char.length;
    el.setSelectionRange(cursor, cursor);
    if (shift) setShift(false);
  }, [shift]);

  const backspace = useCallback(() => {
    const el = focusedRef.current;
    if (!el) return;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    if (start === 0 && end === 0) return;
    const cutFrom = start === end ? Math.max(0, start - 1) : start;
    const next = el.value.slice(0, cutFrom) + el.value.slice(end);
    setReactValue(el, next);
    el.setSelectionRange(cutFrom, cutFrom);
  }, []);

  const onHide = useCallback(() => {
    setOpen(false);
    setShift(false);
    releaseKeyboardLift();
  }, []);

  const onDone = useCallback(() => {
    const el = focusedRef.current;
    el?.blur();
    setOpen(false);
    setShift(false);
    focusedRef.current = null;
    releaseKeyboardLift();
  }, []);

  // Keep the focus handler's view of the nav position current (it reads a ref
  // so the long-lived `focusin` listener doesn't need re-binding on change).
  navPositionRef.current = navPosition;

  const renderKey = (char: string) => {
    const display = mode === "letters" && shift ? char.toUpperCase() : char;
    return (
      <button
        key={`${mode}-${char}`}
        type="button"
        className={styles.key}
        // Important: prevent the focused input from blurring when a key
        // is tapped (which would dismiss the keyboard via the focusin
        // handler above).
        onMouseDown={(e) => e.preventDefault()}
        onTouchStart={(e) => e.preventDefault()}
        onClick={() => insert(display)}
      >
        {display}
      </button>
    );
  };

  const rows = mode === "letters" ? ROW_LETTERS_LOWER : ROW_NUMBERS;

  // Cycle 77: in side-nav mode there's no floating bottom pill to clear,
  // so anchor the keyboard flush to the bottom edge of the content area
  // (the module CSS default `bottom: 64px` + `translateY(... + 64px)` is
  // the bottom-pill offset; override both here only for side mode). The
  // focused-field scroll-into-view logic is unchanged and still works —
  // it scrolls relative to the keyboard's on-screen height, not its
  // offset.
  const sideStyle =
    navPosition === "side"
      ? {
          bottom: 0,
          transform: open ? "translateY(0)" : "translateY(100%)",
        }
      : undefined;

  return (
    <div
      ref={kbdRef}
      className={`${styles.kbd} ${open ? styles.kbdOpen : ""}`}
      style={sideStyle}
      aria-hidden={!open}
      // Prevent any tap inside the keyboard from blurring the text
      // input — we never want the keyboard to dismiss itself when a
      // key is touched.
      onMouseDown={(e) => e.preventDefault()}
      onTouchStart={(e) => {
        // Don't preventDefault on touchstart globally — the buttons
        // need their own click events. Just block the bubble for the
        // wrapper background.
        if (e.target === e.currentTarget) e.preventDefault();
      }}
    >
      <div className={styles.row}>{rows[0]!.map(renderKey)}</div>
      <div className={styles.row}>{rows[1]!.map(renderKey)}</div>
      <div className={styles.row}>
        {mode === "letters" ? (
          <button
            type="button"
            className={`${styles.key} ${styles.keyMod} ${shift ? styles.keyOn : ""}`}
            onMouseDown={(e) => e.preventDefault()}
            onTouchStart={(e) => e.preventDefault()}
            onClick={() => setShift((s) => !s)}
          >
            ⇧
          </button>
        ) : (
          <span className={styles.spacer} />
        )}
        {rows[2]!.map(renderKey)}
        <button
          type="button"
          className={`${styles.key} ${styles.keyMod}`}
          onMouseDown={(e) => e.preventDefault()}
          onTouchStart={(e) => e.preventDefault()}
          onClick={backspace}
          aria-label="Backspace"
        >
          ⌫
        </button>
      </div>
      <div className={styles.row}>
        <button
          type="button"
          className={`${styles.key} ${styles.keyMod}`}
          onMouseDown={(e) => e.preventDefault()}
          onTouchStart={(e) => e.preventDefault()}
          onClick={() => setMode((m) => (m === "letters" ? "numbers" : "letters"))}
        >
          {mode === "letters" ? "123" : "ABC"}
        </button>
        <button
          type="button"
          className={`${styles.key} ${styles.keySpace}`}
          onMouseDown={(e) => e.preventDefault()}
          onTouchStart={(e) => e.preventDefault()}
          onClick={() => insert(" ")}
        >
          space
        </button>
        <button
          type="button"
          className={`${styles.key} ${styles.keyMod}`}
          onMouseDown={(e) => e.preventDefault()}
          onTouchStart={(e) => e.preventDefault()}
          onClick={onHide}
          aria-label="Hide keyboard"
        >
          Hide
        </button>
        <button
          type="button"
          className={`${styles.key} ${styles.keyAccent}`}
          onMouseDown={(e) => e.preventDefault()}
          onTouchStart={(e) => e.preventDefault()}
          onClick={onDone}
        >
          Done
        </button>
      </div>
    </div>
  );
}

/** Set a controlled-input value in a way that React's controlled
 *  component handlers fire correctly. Going through the native
 *  property setter and then dispatching an `input` event is the
 *  documented escape hatch when an external script needs to drive a
 *  React-controlled input. */
function setReactValue(el: EditableElement, value: string): void {
  const proto =
    el instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
  if (setter) setter.call(el, value);
  else el.value = value;
  el.dispatchEvent(new Event("input", { bubbles: true }));
}
